import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase/config';
import { AUDIT_ACTIONS } from '../firebase/auditLogs';
import './Admin.css';

function formatLogDate(createdAt, createdAtIso) {
  try {
    if (createdAt?.toDate) return createdAt.toDate().toLocaleString();
    if (typeof createdAtIso === 'string' && createdAtIso) return new Date(createdAtIso).toLocaleString();
    if (createdAt) return new Date(createdAt).toLocaleString();
    return '';
  } catch {
    return '';
  }
}

function actionLabel(action) {
  switch (action) {
    case AUDIT_ACTIONS.ORDER_MARK_PAID:
      return 'Marked order as paid';
    case AUDIT_ACTIONS.ORDER_MARK_SHIPPED:
      return 'Marked order as shipped';
    case AUDIT_ACTIONS.ORDER_MARK_CONFIRMED:
      return 'Marked order as confirmed';
    case AUDIT_ACTIONS.ORDER_MARK_CANCELLED:
      return 'Cancelled order';
    case AUDIT_ACTIONS.ORDER_MARK_DELIVERED:
      return 'Marked order as delivered';
    case AUDIT_ACTIONS.ORDER_ASSIGN_SHIPPING:
      return 'Assigned order to shipping';
    case AUDIT_ACTIONS.ORDER_DELETE:
      return 'Deleted order';
    default:
      return action || 'Action';
  }
}

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const q = query(collection(db, 'auditLogs'), orderBy('createdAt', 'desc'), limit(200));
        const snap = await getDocs(q);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setLogs(rows);
      } catch (e) {
        console.error('Error fetching audit logs:', e);
        setLogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, []);

  const total = logs.length;

  const displayRows = useMemo(() => {
    return logs.map((l) => {
      const when = formatLogDate(l.createdAt, l.createdAtIso);
      const who = l.actorName || l.actorEmail || l.actorUid || '';
      const role = l.actorRole || '';
      const what = actionLabel(l.action);
      const targetType = l.targetType || '';
      const targetId = l.targetId || '';
      const targetLabel = l.targetLabel || '';

      const targetText = targetLabel || targetId || '';
      const orderDisplay = targetId ? `#${String(targetId).slice(-8)}` : '';
      const targetHref = targetType === 'order' && targetId ? `/admin/orders/${targetId}` : '';

      return {
        id: l.id,
        when,
        who,
        role,
        what,
        targetType,
        targetId,
        targetText,
        orderDisplay,
        targetHref
      };
    });
  }, [logs]);

  if (loading) {
    return (
      <div className="admin-content">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div>
          <h2>Audit Logs</h2>
          <p>Track who did what, and when</p>
        </div>
        <div style={{ color: '#6d7175', fontWeight: 600 }}>Total: {total}</div>
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        {displayRows.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6d7175' }}>
            <h3>No logs yet</h3>
            <p>Logs will appear when staff take actions in the admin panel.</p>
          </div>
        ) : (
          <div className="table-container" style={{ border: 'none', borderRadius: 0 }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Role</th>
                  <th>Action</th>
                  <th>Target</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((r) => (
                  <tr key={r.id}>
                    <td style={{ color: '#6d7175', whiteSpace: 'nowrap' }}>{r.when}</td>
                    <td style={{ fontWeight: 600 }}>{r.who}</td>
                    <td style={{ color: '#6d7175' }}>{r.role}</td>
                    <td>{r.what}</td>
                    <td style={{ fontFamily: 'monospace' }}>
                      {r.targetHref ? (
                        <Link to={r.targetHref} style={{ color: 'inherit', textDecoration: 'underline' }}>
                          {r.orderDisplay || r.targetText}
                        </Link>
                      ) : (
                        r.targetText
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
